import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import {
  NOTE_FORMAT_LABELS,
  type ProgressQueueRow,
  type ProgressQueueView,
  type ProgressToWriteRow,
} from '@ehr/shared';
import { api, ApiError } from '../lib/api.js';
import { noteDate } from '../lib/noteDate.js';
import { NOTES_REMOTE } from '../lib/liveEvents.js';
import type { NoteUser } from '../components/noteSharing.js';
import { fetchNoteUsers } from '../lib/noteUsers.js';
import { NOT_SHARED_HINT, writeBlocked } from '../lib/chartAccess.js';
import { Modal } from '../components/Modal.js';
import { useSession, canManage } from '../auth/session.js';
import { asFormat } from '../components/ClinicalNotesPanel.js';
import { NoteStatusPill } from '../components/NoteStatusPill.js';
import { Dd, MultiDd, type DdOption } from '../components/Dropdown.js';
import { DateField } from '../components/DateTimeField.js';
import { pageOf } from '../lib/pager.js';
import { Pager } from '../components/Pager.js';
import {
  PERIODS,
  STATUS_LABELS,
  WARN_DAYS,
  anyFilterSet,
  applyFilters,
  daysSince,
  defaultList,
  distinctValues,
  emptyFilters,
  filterByClient,
  localizeQueue,
  pruneFilters,
  queueLists,
  resultText,
  sessionWhen,
  sortNewest,
  sortOldest,
  waitingSince,
  waitingText,
  type MultiKey,
  type NoteFilters,
  type QueueList,
  type QueueListId,
} from '../lib/progressQueue.js';

/**
 * Progress Notes (calendar#46 step 9; ehr#181 layout from Chase's PROGRESS_NOTES_09-29-V1):
 * the signed-in user's notes across every client. Count tiles (reminders#2's To write, then
 * To review, Returned to me, My drafts, With reviewer, History) pick the list shown below as
 * one table with a client
 * search and a filter bar (written by, reviewer, template, CPT code, session date, and on
 * History signed vs co-signed). Open lists run oldest first with a days-waiting flag; History
 * runs newest first. To review is the supervisor queue (ehr#189: admins and managers see every
 * note waiting for sign-off, except their own). Rows open the note: reviews and single notes
 * in the client's Clinical Notes, the author's group drafts in the group workspace. A To write
 * row opens that session's note, like the Calendar's Progress Note button.
 * + New note picks a client, then asks which appointment and what kind of note there (ehr#479:
 * as the chart's + Add new, so family / couples, group and case management notes are offered).
 */

const fmtDay = (v: string | Date | null) =>
  v ? noteDate(v).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—';

/** ehr#181 (10-06): the notes table shows 30 a page. */
const PAGE_SIZE = 30;

const PERIOD_OPTIONS: DdOption[] = [
  { value: '', label: 'Any time' },
  ...PERIODS.map((p) => ({ value: p.id, label: p.label })),
];

const SearchIcon = (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    aria-hidden="true"
  >
    <circle cx="11" cy="11" r="7" />
    <path d="m20 20-3.5-3.5" />
  </svg>
);

interface ClientHit {
  id: string;
  firstName: string;
  lastName: string;
  mrn?: string | null;
  email?: string | null;
  phone?: string | null;
  createdAt?: string | null;
}

/** + New note: find the client, then their Clinical Notes tab asks which appointment and kind. */
function NewNoteModal({
  onPick,
  onClose,
}: {
  onPick: (contactId: string) => void;
  onClose: () => void;
}) {
  const [text, setText] = useState('');
  const [hits, setHits] = useState<ClientHit[] | null>(null);
  const [err, setErr] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    const t = setTimeout(() => inputRef.current?.focus(), 0);
    return () => clearTimeout(t);
  }, []);
  useEffect(() => {
    let live = true;
    const q = text.trim();
    const t = setTimeout(() => {
      api
        .get<{ items?: ClientHit[] }>(
          `/contacts?category=client&limit=20${q ? `&search=${encodeURIComponent(q)}` : ''}`,
        )
        .then((r) => {
          if (!live) return;
          setErr('');
          // ehr#181: one row per client, even if a search source repeats one.
          const seen = new Set<string>();
          setHits((r.items ?? []).filter((c) => !seen.has(c.id) && !!seen.add(c.id)));
        })
        .catch(() => live && setErr('Could not load clients.'));
    }, 200);
    return () => {
      live = false;
      clearTimeout(t);
    };
  }, [text]);
  const name = (c: ClientHit) => [c.firstName, c.lastName].filter(Boolean).join(' ') || 'Unnamed';
  return (
    <Modal
      title="New note"
      onClose={onClose}
      className="pq-new-modal"
      actions={
        <button className="btn" onClick={onClose}>
          Cancel
        </button>
      }
    >
      <p className="pq-new-hint">
        Pick the client. Their Clinical Notes tab opens and asks which appointment and what kind of
        note: progress, family or couples, group, case management and the others.
      </p>
      <label className="pq-search pq-new-search">
        {SearchIcon}
        <input
          ref={inputRef}
          type="search"
          value={text}
          placeholder="Search clients by name"
          aria-label="Search clients by name"
          autoComplete="off"
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && hits?.length === 1) onPick(hits[0]!.id);
          }}
        />
      </label>
      <div className="pq-new-list" role="listbox" aria-label="Clients">
        {err ? (
          <p className="pq-new-empty">{err}</p>
        ) : hits === null ? (
          <p className="pq-new-empty">Loading clients…</p>
        ) : hits.length === 0 ? (
          <p className="pq-new-empty">
            {text.trim() ? `No clients match “${text.trim()}”.` : 'No clients yet.'}
          </p>
        ) : (
          hits.map((c) => {
            return (
              <button
                key={c.id}
                type="button"
                role="option"
                aria-selected="false"
                className="pq-new-row"
                onClick={() => onPick(c.id)}
              >
                <span className="pq-new-name">{name(c)}</span>
                {/* ehr#265 (10-06): the client's name only. */}
              </button>
            );
          })
        )}
      </div>
    </Modal>
  );
}

export function ProgressNotesTab({
  onOpenNote,
  onWriteNote,
  onNewNote,
  onChanged,
}: {
  onOpenNote: (row: ProgressQueueRow, mode: 'review' | 'edit' | 'view') => void;
  /** reminders#2 - a To write row: open that session's note (the one it has, or a new one). */
  onWriteNote?: (row: ProgressToWriteRow) => void;
  /** + New note: open this client's Clinical Notes with a blank progress note. */
  onNewNote?: (contactId: string) => void;
  /** The queue was (re)loaded; lets the nav badge follow. */
  onChanged?: (q: ProgressQueueView) => void;
}) {
  const { user } = useSession();
  const manager = Boolean(user && canManage(user.role));
  const lists = queueLists(manager);
  const [q, setQ] = useState<ProgressQueueView | null>(null);
  const [users, setUsers] = useState<NoteUser[]>([]);
  const [err, setErr] = useState('');
  const [active, setActive] = useState<QueueListId | null>(null);
  const [search, setSearch] = useState('');
  // ehr#181 - the filter bar's picks; kept across lists, pruned to what each list offers.
  const [filters, setFilters] = useState<NoteFilters>(emptyFilters);
  const [page, setPage] = useState(1); // ehr#181: 30 notes a page
  const [newOpen, setNewOpen] = useState(false);
  const tileRefs = useRef<Partial<Record<QueueListId, HTMLButtonElement | null>>>({});

  // ehr#343 - a note started or saved elsewhere (the sidebar's live check announces it): reload
  // the lists and counts quietly.
  const changedRef = useRef(onChanged);
  changedRef.current = onChanged;
  useEffect(() => {
    let live = true;
    const reload = () =>
      api
        .get<ProgressQueueView>('/progress-notes/queue')
        .then((v) => {
          if (!live) return;
          setQ(localizeQueue(v));
          changedRef.current?.(v);
        })
        .catch(() => undefined);
    window.addEventListener(NOTES_REMOTE, reload);
    return () => {
      live = false;
      window.removeEventListener(NOTES_REMOTE, reload);
    };
  }, []);
  useEffect(() => {
    let live = true;
    api
      .get<ProgressQueueView>('/progress-notes/queue')
      .then((v) => {
        if (!live) return;
        setQ(localizeQueue(v));
        onChanged?.(v);
      })
      .catch(
        (e: unknown) =>
          live &&
          setErr(
            e instanceof ApiError && e.status === 403
              ? 'Your role cannot view clinical notes.'
              : 'Could not load your progress notes.',
          ),
      );
    // ehr#571: names for clinicians too (they can't read /settings/users).
    void fetchNoteUsers().then((u) => live && setUsers(u));
    return () => {
      live = false;
    };
  }, []);

  const userName = (id: string | null) => {
    const u = users.find((x) => x.id === id);
    return u ? [u.firstName, u.lastName].filter(Boolean).join(' ') || u.email : '—';
  };

  const head = (
    <header className="pq-head">
      {/* ehr#181: no sub-line under the title (Chase, 10-06). */}
      <h1>Progress Notes</h1>
      {onNewNote && (
        <button
          type="button"
          className="btn primary pq-new-btn"
          title="Start a note for a client's session"
          onClick={() => setNewOpen(true)}
        >
          + New note
        </button>
      )}
    </header>
  );
  const newModal = newOpen && onNewNote && (
    <NewNoteModal
      onClose={() => setNewOpen(false)}
      onPick={(id) => {
        setNewOpen(false);
        onNewNote(id);
      }}
    />
  );

  if (err)
    return (
      <div className="pq">
        {head}
        <p className="muted">{err}</p>
        {newModal}
      </div>
    );
  if (!q)
    return (
      <div className="pq">
        {head}
        <p className="muted">Loading progress notes…</p>
        {newModal}
      </div>
    );

  const current = active ?? defaultList(q, lists);
  const list = lists.find((l) => l.id === current)!;
  const isHistory = list.id === 'history';
  const isToWrite = list.id === 'toWrite';
  // A row opens its note; a To write row opens (or starts) the session's note.
  // ehr#622: not a To write row whose client's chart won't open for this user (the row says why).
  const blocked = (r: ProgressQueueRow) => isToWrite && writeBlocked(r as ProgressToWriteRow);
  const open = (r: ProgressQueueRow) => {
    if (blocked(r)) return;
    if (list.mode === 'write') onWriteNote?.(r as ProgressToWriteRow);
    else onOpenNote(r, list.mode);
  };
  const all = q[list.id] ?? [];
  const matched = applyFilters(filterByClient(all, search), filters);
  const rows = isHistory ? sortNewest(matched) : sortOldest(matched, list.id);
  const narrowed = all.length > 0 && (search.trim().length > 0 || anyFilterSet(filters));
  // ehr#181: 30 notes a page.
  const pg = pageOf(rows, page, PAGE_SIZE);

  // Switching lists drops picks the next list can't offer, so it never opens empty.
  const pick = (id: QueueListId) => {
    setActive(id);
    setFilters((f) => pruneFilters(f, q[id] ?? [], id));
    setPage(1);
  };
  const onTileKey = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
    e.preventDefault();
    const i = lists.findIndex((l) => l.id === current);
    const next = lists[(i + (e.key === 'ArrowRight' ? 1 : lists.length - 1)) % lists.length]!;
    pick(next.id);
    tileRefs.current[next.id]?.focus();
  };

  const waiting = (r: ProgressQueueRow) => {
    const days = daysSince(waitingSince(r, list.id));
    return days >= WARN_DAYS ? (
      <span className="pq-tag warn" title={`Waiting ${WARN_DAYS}+ days`}>
        {waitingText(days)}
      </span>
    ) : (
      <span className="pq-cell-main">{waitingText(days)}</span>
    );
  };
  const template = (r: ProgressQueueRow) => (
    <span className={`pq-tag soft fmt-${asFormat(r.format).toLowerCase()}`}>
      {NOTE_FORMAT_LABELS[asFormat(r.format)]}
    </span>
  );
  // Columns after Client, per list (the mockup's sets; Returned shows the review round,
  // since review comments have no open/resolved state to count).
  const cols: [string, (r: ProgressQueueRow) => ReactNode][] = isToWrite
    ? [
        ['Session type', (r) => r.sessionLabel],
        ['Checked out', (r) => fmtDay((r as ProgressToWriteRow).checkedOutAt)],
        ['Since session', waiting],
      ]
    : list.id === 'toReview'
      ? [
          ['Written by', (r) => userName(r.authorId)],
          ['Template', template],
          ['Waiting', waiting],
        ]
      : list.id === 'returned'
        ? [
            ['Reviewer', (r) => userName(r.reviewerId)],
            [
              'Review',
              // ehr#477: a returned note's color (Changes requested), not draft yellow
              (r) => <span className="pq-tag ns-changes">Round {Math.max(1, r.reviewRound)}</span>,
            ],
            ['Returned', (r) => fmtDay(r.updatedAt)],
          ]
        : list.id === 'drafts'
          ? [
              ['Template', template],
              ['Last edited', (r) => fmtDay(r.updatedAt)],
              ['Since session', waiting],
            ]
          : list.id === 'withReviewer'
            ? [
                // ehr#854: the list also holds notes the user sent back, written by others
                ['Written by', (r) => userName(r.authorId)],
                ['Reviewer', (r) => userName(r.reviewerId)],
                ['Submitted', (r) => fmtDay(r.submittedAt)],
                [
                  'Status',
                  (r) =>
                    r.status === 'changes' ? (
                      // ehr#854: sent back, waiting on its author (Changes requested's color)
                      <span
                        className="pq-tag ns-changes"
                        title="Sent back with changes. Waiting for the clinician to resubmit it"
                      >
                        Changes requested
                      </span>
                    ) : (
                      // ehr#477: Pending Review's color
                      <span
                        className="pq-tag ns-pending"
                        title="Locked until your reviewer co-signs or returns it"
                      >
                        Locked
                      </span>
                    ),
                ],
              ]
            : [
                ['Written by', (r) => userName(r.authorId)],
                ['Reviewer', (r) => userName(r.reviewerId)],
                ['Template', template],
                ['Signed on', (r) => fmtDay(r.signedAt ?? r.updatedAt)],
                // ehr#477: signed green, co-signed teal, as in the client's Clinical Notes
                ['Status', (r) => <NoteStatusPill status={r.status} className="pq-tag" />],
              ];

  // ---- Filter bar (ehr#181 V2 / V3): options come from the notes in the open list. ----
  const personOpts = (key: 'authorId' | 'reviewerId'): DdOption[] =>
    distinctValues(all, key).map((id) => ({ value: id, label: userName(id) }));
  const opts: Record<MultiKey, DdOption[]> = {
    authorId: personOpts('authorId'),
    reviewerId: personOpts('reviewerId'),
    template: distinctValues(all, 'template').map((v) => ({
      value: v,
      label: NOTE_FORMAT_LABELS[asFormat(v)],
    })),
    cpt: distinctValues(all, 'cpt').map((v) => ({ value: v, label: v })),
    status: distinctValues(all, 'status').map((v) => ({ value: v, label: STATUS_LABELS[v] ?? v })),
  };
  const setF = (patch: Partial<NoteFilters>) => {
    setFilters((f) => ({ ...f, ...patch }));
    setPage(1);
  };
  const multi = (key: MultiKey, label: string, placeholder: string) => (
    <div className="pq-flt">
      <span className="pq-flt-label">{label}</span>
      <div className="pq-flt-host">
        <MultiDd
          values={filters[key]}
          options={opts[key]}
          onChange={(v) => setF({ [key]: v })}
          placeholder={placeholder}
          searchable={opts[key].length > 8}
          allRow
          clearable
          maxLabel={2}
          emptyText="Nothing to filter"
          ariaLabel={label}
        />
      </div>
    </div>
  );
  const filterBar = (
    <div className="pq-filters" role="group" aria-label="Filter notes">
      {/* A session with no note yet has no author, reviewer or template to filter on. */}
      {!isToWrite && multi('authorId', 'Written by', 'Anyone')}
      {!isToWrite && multi('reviewerId', 'Reviewer', 'Anyone')}
      {!isToWrite && multi('template', 'Template', 'Any template')}
      {multi('cpt', 'CPT code', 'Any code')}
      <div className="pq-flt">
        <span className="pq-flt-label">Session date</span>
        <div className="pq-flt-host">
          <Dd
            value={filters.period}
            options={PERIOD_OPTIONS}
            onChange={(v) => setF(v === 'custom' ? { period: v } : { period: v, from: '', to: '' })}
            placeholder="Any time"
            searchable={false}
            ariaLabel="Session date"
          />
        </div>
      </div>
      {filters.period === 'custom' && (
        <div className="pq-flt-range">
          <div className="pq-flt">
            <span className="pq-flt-label">From</span>
            <div className="pq-flt-host pq-flt-date">
              <DateField
                value={filters.from}
                onChange={(v) => setF({ from: v })}
                placeholder="Start date"
              />
            </div>
          </div>
          <span className="pq-flt-sep">to</span>
          <div className="pq-flt">
            <span className="pq-flt-label">To</span>
            <div className="pq-flt-host pq-flt-date">
              <DateField
                value={filters.to}
                onChange={(v) => setF({ to: v })}
                placeholder="End date"
              />
            </div>
          </div>
        </div>
      )}
      {isHistory && multi('status', 'Status', 'Any status')}
      <div className="pq-filters-end">
        <span className="pq-result-count" aria-live="polite">
          {resultText(rows.length, all.length)}
        </span>
        <button
          type="button"
          className="btn pq-clear"
          disabled={!anyFilterSet(filters) && !search.trim()}
          onClick={() => {
            setFilters(emptyFilters());
            setSearch('');
            setPage(1);
          }}
        >
          Clear filters
        </button>
      </div>
    </div>
  );

  const tile = (l: QueueList) => {
    const count = (q[l.id] ?? []).length;
    const selected = l.id === current;
    const action = l.needsAction && count > 0;
    return (
      <button
        key={l.id}
        ref={(el) => {
          tileRefs.current[l.id] = el;
        }}
        type="button"
        role="tab"
        id={`pq-tile-${l.id}`}
        aria-selected={selected}
        aria-controls="pq-panel"
        tabIndex={selected ? 0 : -1}
        className={`pq-tile${selected ? ' on' : ''}`}
        title={action ? `${l.desc} Action needed.` : l.desc}
        aria-label={`${l.label}: ${count}${action ? ', action needed' : ''}`}
        onClick={() => pick(l.id)}
        onKeyDown={onTileKey}
      >
        {/* ehr#181 (10-06): one compact line per tile, so more notes fit below. A list that
            needs action shows its count as a filled badge. */}
        <span className="pq-tile-label">{l.label}</span>
        <span className={`pq-tile-count${action ? ' action' : ''}`}>{count}</span>
      </button>
    );
  };

  return (
    <div className="pq">
      {head}
      <div className="pq-tiles" role="tablist" aria-label="Note lists">
        {lists.map(tile)}
      </div>

      <section
        className="pq-panel"
        id="pq-panel"
        role="tabpanel"
        aria-labelledby={`pq-tile-${list.id}`}
      >
        <div className="pq-panel-bar">
          <div>
            <h2 className="pq-panel-title">{list.label}</h2>
            {/* ehr#181: no sub-line under To review (Chase). */}
            {list.id !== 'toReview' && <p className="pq-panel-hint">{list.desc}</p>}
          </div>
          <label className="pq-search">
            {SearchIcon}
            <input
              type="search"
              value={search}
              placeholder="Search by client name"
              aria-label="Search by client name"
              autoComplete="off"
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
            />
          </label>
        </div>
        {filterBar}

        {rows.length === 0 ? (
          <div className="pq-empty">
            <p className="pq-empty-title">
              {narrowed ? 'No notes match these filters' : list.emptyTitle}
            </p>
            <p className="pq-empty-body">
              {narrowed ? 'Try a wider date range or clear the filters.' : list.emptyBody}
            </p>
          </div>
        ) : (
          <div className="pq-tbl-wrap">
            <table className="pq-tbl">
              <thead>
                <tr>
                  <th>Client</th>
                  {cols.map(([h]) => (
                    <th key={h}>{h}</th>
                  ))}
                  <th className="pq-col-act" aria-label="Action" />
                </tr>
              </thead>
              <tbody>
                {pg.items.map((r) => (
                  <tr
                    key={r.id}
                    className={blocked(r) ? 'pq-row-blocked' : undefined}
                    onClick={() => open(r)}
                  >
                    <td className="pq-col-client" data-label="Client">
                      <div className="pq-cell-main">
                        {r.contactName}
                        {r.isGroup && <span className="cn-badge group">Group</span>}
                      </div>
                      <div className="pq-cell-sub">
                        {sessionWhen(r)} · {r.cpt ?? r.sessionLabel}
                      </div>
                      {blocked(r) && <div className="pq-cell-blocked">{NOT_SHARED_HINT}</div>}
                    </td>
                    {cols.map(([h, cell]) => (
                      <td key={h} data-label={h}>
                        {cell(r)}
                      </td>
                    ))}
                    <td className="pq-col-act">
                      <button
                        type="button"
                        className={list.primary ? 'btn primary' : 'btn'}
                        disabled={blocked(r)}
                        title={blocked(r) ? NOT_SHARED_HINT : undefined}
                        onClick={(e) => {
                          e.stopPropagation();
                          open(r);
                        }}
                      >
                        {list.action}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Pager
              className="pq-pager"
              page={pg.safePage}
              totalPages={pg.totalPages}
              first={pg.first}
              last={pg.last}
              total={rows.length}
              onPage={setPage}
            />
          </div>
        )}
      </section>
      {newModal}
    </div>
  );
}
